<template>
  <div class="photos-page">
    <StepProgress />

    <SaveAuthModal
      v-model="showAuthModal"
      @authenticated="handleAuthenticated"
      @cancel="showAuthModal = false"
    />

    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Photos
            </v-card-title>
            
            <v-card-text class="pa-6">
              <!-- Photos Section -->
              <div class="mb-8">
                <h3 class="text-h5 mb-4">
                  Add some photos of {{ store.personName || 'your loved one' }} that you would like friends and family to see
                </h3>

                <!-- Drag and Drop Area -->
                <div
                  class="photo-drop-zone"
                  :class="{ 'drag-over': isDragOver }"
                  @dragover.prevent="handleDragOver"
                  @dragleave.prevent="handleDragLeave"
                  @drop.prevent="handleDrop"
                  @click="triggerFileInput"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    accept="image/*,.heic,.HEIC"
                    style="display: none"
                    @change="handleFileSelect"
                  />
                  
                  <v-icon size="64" color="primary" class="mb-4">
                    mdi-cloud-upload-outline
                  </v-icon>
                  <h4 class="text-h6 mb-2">Drop photos here or click to select</h4>
                  <p class="text-body-2 text--secondary">
                    Supports JPG, PNG, GIF, HEIC files
                  </p>
                </div>

                <!-- Photo List -->
                <div v-if="photos.length > 0 || uploadingPhotos.length > 0" class="photo-list mt-6">
                  <!-- Completed Photos -->
                  <div
                    v-for="(photo, index) in photos"
                    :key="`photo-${index}`"
                    class="photo-row mb-4"
                  >
                    <v-row>
                      <v-col cols="12" sm="4" md="3">
                        <div class="photo-container">
                          <v-img
                            :src="photo.url"
                            :alt="photo.name"
                            aspect-ratio="1"
                            cover
                            class="photo-thumbnail"
                          >
                            <template v-slot:placeholder>
                              <v-row class="fill-height ma-0" align="center" justify="center">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                          <div class="photo-overlay">
                            <v-btn
                              icon
                              size="small"
                              color="error"
                              @click="removePhoto(index)"
                              class="remove-btn"
                            >
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="8" md="9">
                        <v-textarea
                          v-model="photo.caption"
                          label="Photo Caption"
                          variant="outlined"
                          rows="4"
                          auto-grow
                          placeholder="Add a caption for this photo..."
                          class="mt-0"
                          @input="updatePhotoCaption(index, $event.target.value)"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </div>
                  
                  <!-- Uploading Photos Placeholders -->
                  <div
                    v-for="(uploadingPhoto, index) in uploadingPhotos"
                    :key="`uploading-${index}`"
                    class="photo-row uploading mb-4"
                  >
                    <v-row>
                      <v-col cols="12" sm="4" md="3">
                        <div class="photo-placeholder">
                          <v-progress-circular 
                            indeterminate 
                            color="primary"
                            size="48"
                            class="mb-2"
                          ></v-progress-circular>
                          <div class="upload-text">
                            <p class="text-body-2">Uploading...</p>
                            <p class="text-caption text--secondary">{{ uploadingPhoto.name }}</p>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="8" md="9">
                        <v-skeleton-loader
                          type="paragraph"
                          class="uploading-caption-placeholder"
                        ></v-skeleton-loader>
                      </v-col>
                    </v-row>
                  </div>
                </div>
              </div>

              <div class="mb-8">
                <h3 class="text-h5 mb-2">Voice memories</h3>
                <p class="text-body-1 mb-4">
                  Share a story in your own voice for friends and family to hear.
                </p>
                <VoiceContributionInput v-model="audios" />
              </div>

              <v-divider class="my-8"></v-divider>

              <!-- Stories Section -->
              <div class="mb-6">
                <h3 class="text-h5 mb-4">Stories</h3>
                <p class="text-body-1 mb-4">
                  Share some meaningful stories about {{ store.personName || 'your loved one' }}
                </p>

                <div
                  v-for="(story, index) in stories"
                  :key="index"
                  class="story-item mb-4"
                >
                  <v-textarea
                    v-model="stories[index]"
                    :label="`Story ${index + 1}`"
                    variant="outlined"
                    rows="4"
                    auto-grow
                    class="mb-2"
                  ></v-textarea>
                  
                  <div class="story-actions">
                    <v-btn
                      v-if="stories.length > 1"
                      color="error"
                      variant="text"
                      size="small"
                      @click="removeStory(index)"
                    >
                      <v-icon left>mdi-delete</v-icon>
                      Remove Story
                    </v-btn>
                  </div>
                </div>

                <v-btn
                  color="primary"
                  variant="outlined"
                  @click="addStory"
                  class="mt-2"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Another Story
                </v-btn>
              </div>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="previousStep"
                class="mr-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>

              <v-spacer></v-spacer>

              <!-- Save button (visible when not logged in OR when logged in with changes) -->
              <v-btn
                v-if="!isEditMode || (isEditMode && hasChanges)"
                variant="outlined"
                color="primary"
                size="large"
                :disabled="saving || (isEditMode && !hasChanges)"
                @click="handleSave"
                class="mr-3"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save
              </v-btn>

              <!-- Next Step button (always visible when not in edit mode, OR when in edit mode but hasn't visited Message page yet) -->
              <v-btn
                v-if="!isEditMode || (isEditMode && !hasVisitedMessagePage)"
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :disabled="saving"
                @click="nextStep"
                class="px-8"
              >
                Next Step
                <v-icon right class="ml-2">mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showErrorSnackbar"
      :timeout="5000"
      color="error"
      location="top"
    >
      {{ errorMessage }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showErrorSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import StepProgress from '../components/StepProgress.vue'
import SaveAuthModal from '../components/SaveAuthModal.vue'
import VoiceContributionInput from '../components/media/VoiceContributionInput.vue'
import apiService from '../services/api'
import { isSupportedImage, normalizeImageFile, readFileAsDataUrl } from '../services/photoUpload'

const router = useRouter()
const store = useMemorialStore()

// Reactive data
const photos = ref([])
const audios = computed({
  get: () => store.audios,
  set: value => store.updateAudios(value)
})
const uploadingPhotos = ref([])
const stories = ref([''])
const isDragOver = ref(false)
const fileInput = ref(null)
const saving = ref(false)
const showAuthModal = ref(false)
const showErrorSnackbar = ref(false)
const errorMessage = ref('')

// File handling methods
const handleDragOver = () => {
  isDragOver.value = true
}

const handleDragLeave = () => {
  isDragOver.value = false
}

const handleDrop = (e) => {
  isDragOver.value = false
  const files = Array.from(e.dataTransfer.files)
  processFiles(files)
}

const triggerFileInput = () => {
  fileInput.value.click()
}

const handleFileSelect = (e) => {
  const files = Array.from(e.target.files)
  processFiles(files)
}

const processFiles = async (files) => {
  for (const file of files) {
    if (!isSupportedImage(file)) continue

    // Add placeholder for uploading photo
    const uploadingPhoto = {
      name: file.name,
      id: Date.now() + Math.random() // Unique ID for tracking
    }
    uploadingPhotos.value.push(uploadingPhoto)

    try {
      const processedFile = await normalizeImageFile(file)
      const dataUrl = await readFileAsDataUrl(processedFile)

      // Remove from uploading list
      const uploadingIndex = uploadingPhotos.value.findIndex(up => up.id === uploadingPhoto.id)
      if (uploadingIndex !== -1) {
        uploadingPhotos.value.splice(uploadingIndex, 1)
      }

      // Add to completed photos
      const photo = {
        file: processedFile,
        name: processedFile.name,
        url: dataUrl,
        caption: ''
      }
      photos.value.push(photo)
      store.addPhoto(photo)
    } catch (error) {
      console.error('Error processing file:', error)

      // Remove from uploading list on error
      const uploadingIndex = uploadingPhotos.value.findIndex(up => up.id === uploadingPhoto.id)
      if (uploadingIndex !== -1) {
        uploadingPhotos.value.splice(uploadingIndex, 1)
      }

      // Show user-friendly error message
      errorMessage.value = `Error processing ${file.name}. Please try a different file format.`
      showErrorSnackbar.value = true
    }
  }
}

const removePhoto = (index) => {
  photos.value.splice(index, 1)
  store.removePhoto(index)
}

const updatePhotoCaption = (index, caption) => {
  if (photos.value[index]) {
    photos.value[index].caption = caption
    store.updatePhotoCaption(index, caption)
  }
}

// Story methods
const addStory = () => {
  stories.value.push('')
  store.addStory()
}

const removeStory = (index) => {
  if (stories.value.length > 1) {
    stories.value.splice(index, 1)
    store.removeStory(index)
  }
}

// Computed properties
const isEditMode = computed(() => {
  // User is in edit mode if they are logged in (completed account creation)
  // AND have an existing memorial they're editing
  // First-time users walking through the steps should see "Next Step" even with memorial UUID
  return store.isAccountCreated && store.isEmailVerified && store.memorialUuid
})

const hasChanges = computed(() => store.hasUnsavedChangesComputed)

const hasVisitedMessagePage = computed(() => {
  // Photos is step 2, Message is step 3
  // If user's maxReachedStep is greater than 2, they've visited Message page
  return store.currentStep > 2 || store.maxReachedStep > 2
})

// Navigation methods
const nextStep = () => {
  store.goToStep(3) // Message step
  router.push('/message')
}

const previousStep = () => {
  store.goToStep(1) // Who step
  router.push('/who')
}

const handleSave = () => {
  // If user is not logged in, show auth modal
  if (!store.isAccountCreated || !store.isEmailVerified || !store.apiToken) {
    showAuthModal.value = true
  } else {
    // User is logged in, save directly
    return saveMemorialFromButton()
  }
}

const handleAuthenticated = async (authResponse) => {
  // User has authenticated, now save the memorial

  // Update store with user data
  store.setApiAuth(authResponse.token, authResponse.user)

  // Now save the memorial
  return await saveMemorialFromButton()
}

const saveMemorialFromButton = async () => {
  if (saving.value) return

  saving.value = true

  try {
    const reachedStep = Math.max(store.maxReachedStep, store.currentStep || 0)
    store.maxReachedStep = reachedStep

    // Build memorial data from store
    const memorialData = {
      uuid: store.memorialUuid,
      userUuid: store.user?.uuid || null,
      adminUuids: null,
      nameOfPerson: store.personName || null,
      memorialDate: store.memorialDate || null,
      address: store.memorialAddress || null,
      details: store.memorialDetails || null,
      maxReachedStep: reachedStep,
      mediaUuids: null, // Photos will be uploaded separately
      stories: buildStoriesArray(),
      contacts: buildContactsArray(),
      hostMessage: store.personalMessage || null,
      hostVideoMessageUuid: null,
      stripeCustomerId: null
    }


    // Use create if no UUID exists, otherwise edit
    const response = store.memorialUuid
      ? await apiService.editMemorial(memorialData, store.apiToken)
      : await apiService.createMemorial(memorialData, store.apiToken)

    if (!response.success) {
      throw new Error(response.msg || 'Failed to save memorial')
    }

    console.log('Memorial saved successfully:', response)
    await store.persistMaxReachedStep()

    // Store the memorial UUID if this was a new memorial
    if (response.memorial?.uuid && !store.memorialUuid) {
      store.setMemorialUuid(response.memorial.uuid)
      // Also update share slug if provided
      if (response.memorial.shareSlug) {
        store.setShareSlug(response.memorial.shareSlug)
      }
    }

    // Upload profile photo if present
    if (store.profilePhotoFile && store.memorialUuid) {
      try {
        console.log('Uploading profile photo...')
        const profilePhotoResponse = await apiService.uploadPhoto(
          store.memorialUuid,
          store.profilePhotoFile,
          '', // No caption for profile photo
          store.apiToken,
          {
            isProfilePhoto: true
          }
        )

        if (!profilePhotoResponse.success) {
          throw new Error(profilePhotoResponse.msg || 'Failed to upload profile photo')
        }

        console.log('Profile photo uploaded successfully:', profilePhotoResponse)
      } catch (profilePhotoError) {
        console.error('Profile photo upload error:', profilePhotoError)
        // Continue with other uploads even if profile photo fails
      }
    }

    // Now upload photos if we have any
    if (photos.value.length > 0 && store.memorialUuid) {
      console.log(`Uploading ${photos.value.length} photos...`)

      for (const photo of photos.value) {
        if (photo.file) {
          try {
            console.log('Uploading photo:', photo.name)
            const uploadResponse = await apiService.uploadPhoto(
              store.memorialUuid,
              photo.file,
              photo.caption || '',
              store.apiToken
            )

            if (uploadResponse.success && uploadResponse.mediaUuid) {
              console.log('Photo uploaded successfully:', uploadResponse.mediaUuid)
            } else {
              console.error('Photo upload failed:', uploadResponse.msg || 'Unknown error')
            }
          } catch (error) {
            console.error('Photo upload error:', error)
          }
        }
      }
    }

    if (audios.value.length > 0 && store.memorialUuid) {
      for (const audio of audios.value) {
        if (!audio.file) continue
        const uploadResponse = await apiService.uploadAudio(
          store.memorialUuid,
          audio.file,
          audio.caption || '',
          store.apiToken
        )
        if (!uploadResponse.success) {
          throw new Error(uploadResponse.msg || `Failed to upload ${audio.name}`)
        }
        audio.id = uploadResponse.media?.uuid
        audio.file = null
        audio.serverData = uploadResponse.media
      }
      store.updateAudios(audios.value)
    }

    // Update change tracking after successful save
    store.markChangesSaved()

    return { success: true, memorial: response.memorial }

  } catch (error) {
    console.error('Memorial save error:', error)
    return { success: false, error: error.message }
  } finally {
    saving.value = false
  }
}

// eslint-disable-next-line no-unused-vars
const saveMemorial = async () => {
  if (saving.value) return
  
  saving.value = true
  
  try {
    let mediaUuids = []
    
    // Handle photos - both existing server photos and new uploads
    if (store.memorialUuid && photos.value.length > 0) {
      console.log(`Processing ${photos.value.length} photos...`)
      
      for (const photo of photos.value) {
        if (photo.file) {
          // This is a new photo that needs to be uploaded
          try {
            console.log('Uploading new photo:', photo.name)
            const uploadResponse = await apiService.uploadPhoto(store.memorialUuid, photo.file, photo.caption || '', store.apiToken)
            
            if (uploadResponse.success && uploadResponse.mediaUuid) {
              mediaUuids.push(uploadResponse.mediaUuid)
              console.log('Photo uploaded successfully:', uploadResponse.mediaUuid)
            } else {
              console.error('Photo upload failed:', uploadResponse.msg || 'Unknown error')
            }
          } catch (error) {
            console.error('Photo upload error:', error)
          }
        } else if (photo.id || photo.serverData) {
          // This is an existing photo from the server
          const mediaUuid = photo.id || photo.serverData.uuid
          mediaUuids.push(mediaUuid)
          console.log('Including existing photo:', mediaUuid)
          
          // Check if the caption has changed and update it if necessary
          const originalCaption = photo.serverData?.title || ''
          const currentCaption = photo.caption || ''
          
          if (originalCaption !== currentCaption) {
            try {
              console.log(`Updating caption for existing photo ${mediaUuid}: "${originalCaption}" -> "${currentCaption}"`)
              const updateResponse = await apiService.updateMediaTitle(mediaUuid, currentCaption, store.apiToken)
              
              if (updateResponse.success) {
                console.log('Photo caption updated successfully:', mediaUuid)
              } else {
                console.error('Photo caption update failed:', updateResponse.msg || 'Unknown error')
              }
            } catch (error) {
              console.error('Photo caption update error:', error)
            }
          }
        }
      }
    }
    
    // Handle host video - both existing server video and new uploads
    let hostVideoMessageUuid = null
    if (store.videoMessage && store.memorialUuid) {
      if (store.videoMessage instanceof File) {
        // This is a new video that needs to be uploaded
        try {
          console.log('Uploading new host video...')
          const videoResponse = await apiService.uploadVideoMessage(store.memorialUuid, store.videoMessage, '', store.apiToken)
          
          if (videoResponse.success && videoResponse.mediaUuid) {
            hostVideoMessageUuid = videoResponse.mediaUuid
            console.log('Host video uploaded successfully:', hostVideoMessageUuid)
          } else {
            console.error('Host video upload failed:', videoResponse.msg || 'Unknown error')
          }
        } catch (error) {
          console.error('Host video upload error:', error)
        }
      } else if (store.videoMessage.uuid || store.videoMessage.isServerVideo) {
        // This is an existing video from the server
        hostVideoMessageUuid = store.videoMessage.uuid
        console.log('Including existing host video:', hostVideoMessageUuid)
      }
    }
    
    const reachedStep = Math.max(store.maxReachedStep, store.currentStep || 0)
    store.maxReachedStep = reachedStep

    // Build memorial data from store
    const memorialData = {
      uuid: store.memorialUuid,
      userUuid: store.user?.uuid || null,
      adminUuids: null,
      nameOfPerson: store.personName || null,
      address: store.memorialAddress || null,
      details: store.memorialDetails || null,
      maxReachedStep: reachedStep,
      mediaUuids: mediaUuids.length > 0 ? mediaUuids : null,
      stories: buildStoriesArray(),
      contacts: buildContactsArray(),
      hostMessage: store.personalMessage || null,
      hostVideoMessageUuid: hostVideoMessageUuid,
      stripeCustomerId: null
    }

    
    const response = await apiService.editMemorial(memorialData, store.apiToken)
    
    if (!response.success) {
      throw new Error(response.msg || 'Failed to save memorial')
    }
    
    console.log('Memorial saved successfully:', response)
    await store.persistMaxReachedStep()
    
    // Update change tracking after successful save
    store.markChangesSaved()
    
    // Show success message or redirect
    // For now, just log success and stay on page
    
  } catch (error) {
    console.error('Memorial save error:', error)
    // TODO: Show error message to user
  } finally {
    saving.value = false
  }
}

const buildStoriesArray = () => {
  return store.stories
    .filter(story => story && story.trim())
    .map(story => ({
      uuid: null,
      text: story.trim()
    }))
}

const buildContactsArray = () => {
  return store.contacts
    .filter(contact => contact && contact.trim())
    .map(contact => {
      const contactData = {
        uuid: null,
        email: null,
        phone: null,
        firstName: null,
        surname: null
      }
      
      // Check if it's an email or phone
      if (/.+@.+\..+/.test(contact.trim())) {
        contactData.email = contact.trim()
      } else if (/^[+]?[\s\-()]*([0-9][\s\-()]*){7,}/.test(contact.trim())) {
        contactData.phone = contact.trim()
      }
      
      return contactData
    })
    .filter(contact => contact.email || contact.phone) // Only include valid contacts
}

// Watch for story changes
watch(stories, (newStories) => {
  newStories.forEach((storyContent, index) => {
    store.updateStory(index, storyContent)
  })
}, { deep: true })

// Initialize from store
onMounted(async () => {
  // Initialize page tracking for logged-in users
  if (store.isAccountCreated && store.isEmailVerified && store.apiToken) {
    store.enterPage('photos')
    
    // If user has a memorial UUID, always reload from server to ensure fresh data
    if (store.memorialUuid) {
      console.log('Reloading memorial data from server...')
      const loaded = await store.loadUserMemorialFromServer()
      if (loaded) {
        console.log('Memorial data reloaded successfully')
        // Update local data after server reload
        photos.value = [...store.photos]
        stories.value = store.stories.length > 0 ? [...store.stories] : ['']
      }
    }
  }
  
  // Initialize local component data from store (if not already updated above)
  if (photos.value.length === 0) {
    photos.value = [...store.photos]
  }
  if (stories.value.length === 0 || (stories.value.length === 1 && stories.value[0] === '')) {
    console.log('Loading stories from store:', store.stories)
    stories.value = store.stories.length > 0 ? [...store.stories] : ['']
  }
  store.goToStep(2) // Set current step to Photos
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;
.photos-page {
  @include page-container;
}

.main-card {
  @include main-card;
  
  .v-card-title {
    @include section-header;
  }
}

.photo-drop-zone {
  border: 1px dashed $border-dark;
  border-radius: $border-radius;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all $transition-duration $transition-timing;
  background: $gray-50;
  
  &:hover,
  &.drag-over {
    border-color: $primary;
    background: rgba($primary, 0.1);
  }
}

.photo-list {
  .photo-row {
    border: 1px solid $border;
    border-radius: $border-radius;
    padding: 1rem;
    background: $gray-50;
    
    &.uploading {
      .photo-placeholder {
        aspect-ratio: 1;
        border: 2px dashed $border;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: rgba($primary, 0.05);
        padding: 1rem;
        text-align: center;
        
        .upload-text {
          p {
            margin: 0;
            line-height: 1.2;
          }
        }
      }
      
      .uploading-caption-placeholder {
        height: 100px;
        opacity: 0.5;
      }
    }
  }
  
  .photo-container {
    position: relative;
  }
}

.photo-thumbnail {
  border-radius: 8px;
  overflow: hidden;
}

.photo-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-item:hover .photo-overlay {
  opacity: 1;
}

.remove-btn {
  background: rgba(255, 255, 255, 0.9) !important;
}

.photo-name {
  text-align: center;
  word-break: break-word;
}

.story-item {
  border: $border-width-thin solid $border;
  border-radius: $border-radius;
  padding: 1rem;
  background: $gray-50;
}

.story-actions {
  display: flex;
  justify-content: flex-end;
}

@include mobile {
  .photo-drop-zone {
    padding: 2rem 1rem;
  }
  
  .photo-list {
    .photo-row {
      padding: 0.75rem;
    }
  }
  
  .pa-6 {
    padding: 1rem !important;
  }
}
</style>
